/**
 * ScoreboardTab — Admin tab for managing ticker configs and groups.
 * Each config scrapes a MaxPreps scores page and produces a JSON endpoint.
 * Groups merge multiple configs into one combined JSON feed.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus, Trash2, Copy, ExternalLink, Link2, Loader2,
  ChevronDown, ChevronUp, Layers, Edit3, EyeOff, RotateCcw, ArrowUp, ArrowDown, Rss, Type, Monitor, Palette, ImageIcon, Star, X, Check
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getAuthHeader = () => {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied to clipboard");
};

/** Resolve a logo URL for display: uploaded logos are served directly, external logos go through proxy */
const resolveLogoSrc = (url) => {
  if (!url) return "";
  const isUpload = url.includes("/api/scoreboard/logo-file/");
  if (isUpload) {
    // Uploaded logo — serve directly (handle both relative and absolute)
    return url.startsWith("http") ? url : `${API.replace("/api", "")}${url}`;
  }
  // External logo — route through proxy for CORS + white-bg removal
  return `${API}/scoreboard/logo-proxy?url=${encodeURIComponent(url)}`;
};

/* ── Ticker Config Form ── */
const ConfigForm = ({ onSave, initial = null, onCancel }) => {
  const [form, setForm] = useState(
    initial || { title: "", region: "", sport: "FB", maxpreps_link: "", ticker_logo: "", ticker_sponsor: "", ticker_speed: 5 }
  );
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.maxpreps_link) {
      toast.error("Title and MaxPreps link are required");
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      if (!initial) setForm({ title: "", region: "", sport: "FB", maxpreps_link: "", ticker_logo: "", ticker_sponsor: "", ticker_speed: 5 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 bg-zinc-900/60 border border-white/10 p-4" data-testid="scoreboard-config-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Title <span className="normal-case text-zinc-600">— shown in ticker text transitions</span></label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Tyler Area Football"
            className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="scoreboard-title-input"
          />
        </div>
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Region <span className="normal-case text-zinc-600">— shown on the ticker region block</span></label>
          <input
            type="text"
            value={form.region}
            onChange={(e) => setForm({ ...form, region: e.target.value })}
            placeholder="Tyler, TX"
            className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="scoreboard-region-input"
          />
        </div>
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Sport</label>
          <select
            value={form.sport}
            onChange={(e) => setForm({ ...form, sport: e.target.value })}
            className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="scoreboard-sport-select"
          >
            <option value="FB">Football</option>
            <option value="BKB">Basketball</option>
            <option value="BB">Baseball</option>
            <option value="SB">Softball</option>
            <option value="SOC">Soccer</option>
            <option value="VB">Volleyball</option>
            <option value="WR">Wrestling</option>
            <option value="TF">Track & Field</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">MaxPreps Scores Link</label>
        <input
          type="text"
          value={form.maxpreps_link}
          onChange={(e) => setForm({ ...form, maxpreps_link: e.target.value })}
          placeholder="https://www.maxpreps.com/tx/dma/tyler/football/scores/?dmaid=..."
          className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm font-mono text-xs"
          data-testid="scoreboard-link-input"
        />
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Ticker Logo URL</label>
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={form.ticker_logo || ""}
            onChange={(e) => setForm({ ...form, ticker_logo: e.target.value })}
            placeholder="https://example.com/logo.png"
            className="flex-1 bg-black/40 border border-white/10 text-white px-3 py-2 text-sm font-mono text-xs"
            data-testid="scoreboard-ticker-logo-input"
          />
          {form.ticker_logo && (
            <div className="w-10 h-10 bg-black/60 border border-white/10 flex items-center justify-center flex-shrink-0">
              <img src={form.ticker_logo} alt="Logo" className="max-w-full max-h-full object-contain" />
            </div>
          )}
        </div>
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Ticker Sponsor Banner URL <span className="text-zinc-700">(200×48 right block)</span></label>
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={form.ticker_sponsor || ""}
            onChange={(e) => setForm({ ...form, ticker_sponsor: e.target.value })}
            placeholder="https://example.com/sponsor-banner.png"
            className="flex-1 bg-black/40 border border-white/10 text-white px-3 py-2 text-sm font-mono text-xs"
            data-testid="scoreboard-ticker-sponsor-input"
          />
          {form.ticker_sponsor && (
            <div className="h-10 bg-black/60 border border-white/10 flex items-center justify-center flex-shrink-0 px-2">
              <img src={form.ticker_sponsor} alt="Sponsor" className="max-h-full object-contain" style={{ maxWidth: 100 }} />
            </div>
          )}
        </div>
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Ticker Speed (seconds per game)</label>
        <select
          value={form.ticker_speed || 5}
          onChange={(e) => setForm({ ...form, ticker_speed: parseInt(e.target.value) })}
          className="bg-black/40 border border-white/10 text-white px-3 py-2 text-sm"
          data-testid="scoreboard-ticker-speed"
        >
          <option value={3}>3s — Fast</option>
          <option value={5}>5s — Normal</option>
          <option value={8}>8s — Slow</option>
          <option value={10}>10s — Very Slow</option>
          <option value={15}>15s</option>
        </select>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm transition-colors"
          data-testid="scoreboard-save-btn"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          {initial ? "Update" : "Add Ticker"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-zinc-500 hover:text-white px-4 py-2 text-sm transition-colors">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

/* ── Ticker Config Card ── */
const ConfigCard = ({ config, groups, onDelete, onRefresh, onUpdate }) => {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [overrides, setOverrides] = useState({});        // {game_url: {away_team, home_team}}
  const [hiddenGames, setHiddenGames] = useState([]);     // [game_url, ...]
  // Refs to always have the latest values for async save operations (avoids stale closures)
  const overridesRef = useRef(overrides);
  const hiddenGamesRef = useRef(hiddenGames);
  useEffect(() => { overridesRef.current = overrides; }, [overrides]);
  useEffect(() => { hiddenGamesRef.current = hiddenGames; }, [hiddenGames]);
  const [editingGame, setEditingGame] = useState(null);   // game_url being edited
  const [editForm, setEditForm] = useState({});
  const [savingOverrides, setSavingOverrides] = useState(false);
  const [savedGame, setSavedGame] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null); // {title, region}
  // Which groups this ticker belongs to
  const memberGroups = (groups || []).filter(g => (g.scoreboard_ids || []).includes(config.id));
  const nonMemberGroups = (groups || []).filter(g => !(g.scoreboard_ids || []).includes(config.id));
  const [showHidden, setShowHidden] = useState(false);
  const [gameOrder, setGameOrder] = useState(config.game_order || []);
  const [customTexts, setCustomTexts] = useState(config.custom_texts || []);
  const [newText, setNewText] = useState("");
  const [showTickerPreview, setShowTickerPreview] = useState(false);
  const [tickerKey, setTickerKey] = useState(0);
  const [tickerScale, setTickerScale] = useState(0.5);
  const tickerContainerRef = useRef(null);
  const jsonUrl = `${process.env.REACT_APP_BACKEND_URL}/api/scoreboard/${config.id}/scores.json`;
  const tickerUrl = `${process.env.REACT_APP_BACKEND_URL}/api/scoreboard/${config.id}/ticker.html`;

  useEffect(() => {
    if (!showTickerPreview || !tickerContainerRef.current) return;
    const measure = () => {
      const w = tickerContainerRef.current?.clientWidth;
      if (w) setTickerScale(w / 1920);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(tickerContainerRef.current);
    return () => ro.disconnect();
  }, [showTickerPreview]);

  const loadOverrides = async () => {
    try {
      const res = await axios.get(`${API}/scoreboard/configs/${config.id}/overrides`, { headers: getAuthHeader() });
      setOverrides(res.data.game_overrides || {});
      setHiddenGames(res.data.hidden_games || []);
    } catch {}
  };

  /* ── Game Order ── */
  const moveGame = async (idx, direction) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= rawGames.length) return;
    // Swap in the display list
    const newGames = [...rawGames];
    [newGames[idx], newGames[newIdx]] = [newGames[newIdx], newGames[idx]];
    setRawGames(newGames);
    // Build order from game URLs and save
    const order = newGames.map(g => g.game_url);
    setGameOrder(order);
    try {
      await axios.put(`${API}/scoreboard/configs/${config.id}/game-order`, order, { headers: getAuthHeader() });
    } catch { toast.error("Failed to save order"); }
  };

  /* ── Custom Texts ── */
  const addCustomText = async () => {
    if (!newText.trim()) return;
    const updated = [...customTexts, newText.trim()];
    setCustomTexts(updated);
    setNewText("");
    try {
      await axios.put(`${API}/scoreboard/configs/${config.id}/custom-texts`, updated, { headers: getAuthHeader() });
      toast.success("Custom text added");
    } catch { toast.error("Failed to save"); }
  };

  const removeCustomText = async (idx) => {
    const updated = customTexts.filter((_, i) => i !== idx);
    setCustomTexts(updated);
    try {
      await axios.put(`${API}/scoreboard/configs/${config.id}/custom-texts`, updated, { headers: getAuthHeader() });
      toast.success("Custom text removed");
    } catch { toast.error("Failed to save"); }
  };

  const handleRefresh = async () => {
    setLoadingPreview(true);
    try {
      await loadOverrides();
      const res = await axios.post(`${API}/scoreboard/configs/${config.id}/refresh`, {}, { headers: getAuthHeader() });
      setPreview(res.data);
      setShowPreview(true);
      toast.success(`Refreshed: ${res.data.games?.length || 0} games found`);
    } catch (err) {
      toast.error("Refresh failed");
    } finally {
      setLoadingPreview(false);
    }
  };

  const startEditing = (game) => {
    const gurl = game.game_url;
    const ov = overrides[gurl] || {};
    setEditingGame(gurl);
    setSavedGame(null);    setEditForm({
      away_team: ov.away_team || game.away_team,
      home_team: ov.home_team || game.home_team,
      away_color: ov.away_color || game.away_color || "",
      home_color: ov.home_color || game.home_color || "",
      away_logo: ov.away_logo || game.away_logo || "",
      home_logo: ov.home_logo || game.home_logo || "",
      featured: ov.featured ?? false,
      _orig_away_color: game.away_color || "",
      _orig_home_color: game.home_color || "",
      _orig_away_logo: game.away_logo || "",
      _orig_home_logo: game.home_logo || "",
    });
  };

  const handleLogoUpload = async (file, side) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/scoreboard/logo-upload`, formData, {
        headers: getAuthHeader(),
      });
      const url = res.data.url;  // Store relative path (e.g. /api/scoreboard/logo-file/...)
      setEditForm(prev => ({ ...prev, [`${side}_logo`]: url }));
      toast.success("Logo uploaded");
    } catch {
      toast.error("Logo upload failed");
    }
  };

  const saveConfigMeta = async () => {
    if (!editingConfig) return;
    try {
      await axios.put(`${API}/scoreboard/configs/${config.id}`,
        { title: editingConfig.title, region: editingConfig.region, ticker_logo: editingConfig.ticker_logo, ticker_sponsor: editingConfig.ticker_sponsor },
        { headers: getAuthHeader() }
      );
      onUpdate?.();
      toast.success("Ticker updated");
      setEditingConfig(null);
    } catch {
      toast.error("Failed to update ticker");
    }
  };

  const toggleGroupMembership = async (group, add) => {
    const ids = add
      ? [...(group.scoreboard_ids || []), config.id]
      : (group.scoreboard_ids || []).filter(id => id !== config.id);
    try {
      await axios.put(`${API}/scoreboard/groups/${group.id}`,
        { ...group, scoreboard_ids: ids },
        { headers: getAuthHeader() }
      );
      onUpdate?.();
      toast.success(add ? `Added to "${group.title}"` : `Removed from "${group.title}"`);
    } catch {
      toast.error("Failed to update group");
    }
  };

  const saveGameEdit = async () => {
    const latestOverrides = { ...overridesRef.current };
    const latestHidden = [...hiddenGamesRef.current];
    const { _orig_away_color, _orig_home_color, _orig_away_logo, _orig_home_logo, ...fields } = editForm;
    // Build the override: keep non-empty values, explicitly remove empty ones
    const existing = latestOverrides[editingGame] || {};
    const merged = { ...existing };
    for (const [k, v] of Object.entries(fields)) {
      if (v !== "" && v !== null && v !== undefined) {
        merged[k] = v;
      } else {
        // User cleared this field — remove the override so original value is used
        delete merged[k];
      }
    }
    // If no overrides remain, remove the game entry entirely
    if (Object.keys(merged).length === 0) {
      delete latestOverrides[editingGame];
    } else {
      latestOverrides[editingGame] = merged;
    }
    setSavingOverrides(true);
    try {
      await axios.put(`${API}/scoreboard/configs/${config.id}/overrides`,
        { game_overrides: latestOverrides, hidden_games: latestHidden },
        { headers: getAuthHeader() }
      );
      setOverrides(latestOverrides);
      setSavedGame(editingGame);
      toast.success("Game override saved");
    } catch {
      toast.error("Failed to save override");
    } finally {
      setSavingOverrides(false);
    }
  };

  const clearGameOverride = async (gameUrl) => {
    const latestOverrides = { ...overridesRef.current };
    const latestHidden = [...hiddenGamesRef.current];
    delete latestOverrides[gameUrl];
    try {
      await axios.put(`${API}/scoreboard/configs/${config.id}/overrides`,
        { game_overrides: latestOverrides, hidden_games: latestHidden },
        { headers: getAuthHeader() }
      );
      setOverrides(latestOverrides);
      toast.success("Override cleared — original names restored");
    } catch {
      toast.error("Failed to clear override");
    }
  };

  const hideGame = async (gameUrl) => {
    const latestOverrides = { ...overridesRef.current };
    const newHidden = [...hiddenGamesRef.current, gameUrl];
    try {
      await axios.put(`${API}/scoreboard/configs/${config.id}/overrides`,
        { game_overrides: latestOverrides, hidden_games: newHidden },
        { headers: getAuthHeader() }
      );
      setHiddenGames(newHidden);
      toast.success("Game hidden from output");
    } catch {
      toast.error("Failed to hide game");
    }
  };

  const restoreGame = async (gameUrl) => {
    const latestOverrides = { ...overridesRef.current };
    const newHidden = hiddenGamesRef.current.filter(u => u !== gameUrl);
    try {
      await axios.put(`${API}/scoreboard/configs/${config.id}/overrides`,
        { game_overrides: latestOverrides, hidden_games: newHidden },
        { headers: getAuthHeader() }
      );
      setHiddenGames(newHidden);
      toast.success("Game restored to output");
    } catch {
      toast.error("Failed to restore game");
    }
  };

  // Get the raw scraped games (before overrides) for preview
  // We re-fetch raw on refresh, but display with overrides applied
  const getDisplayName = (game, field) => {
    const ov = overrides[game.game_url];
    if (ov && ov[field] != null) return ov[field];
    return game[field];
  };

  const isHidden = (game) => hiddenGames.includes(game.game_url);
  const hasOverride = (game) => !!overrides[game.game_url];

  // We need ALL games (including hidden) for the admin view
  // The preview from the API already has overrides/hidden applied, so we need raw data
  const [rawGames, setRawGames] = useState([]);

  const handleRefreshFull = async () => {
    setLoadingPreview(true);
    try {
      await loadOverrides();
      // Get raw scraped data by clearing cache first, then fetching  
      const res = await axios.post(`${API}/scoreboard/configs/${config.id}/refresh`, {}, { headers: getAuthHeader() });
      // The refresh endpoint returns post-override data, but we need pre-override
      // We'll reconstruct by re-adding hidden games from raw scrape
      // Actually, let's just fetch the raw JSON and keep hidden games separate
      setPreview(res.data);
      
      // For admin view, we re-scrape by fetching with a trick — load raw from refresh  
      // The raw games are in the response but with overrides applied.
      // We need to get the raw games. Let's just store what we get and handle display.
      setRawGames(res.data.games || []);
      // Initialize game order if not set
      if (!gameOrder.length && res.data.games?.length) {
        setGameOrder(res.data.games.map((_, i) => i));
      }
      setShowPreview(true);
      toast.success(`Refreshed: ${res.data.games?.length || 0} games in output`);
    } catch (err) {
      toast.error("Refresh failed");
    } finally {
      setLoadingPreview(false);
    }
  };

  return (
    <div className="bg-zinc-900/60 border border-white/10 p-4 space-y-3" data-testid={`scoreboard-config-${config.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-white text-sm font-bold truncate">{config.title}</h4>
          <div className="flex gap-3 text-[10px] text-zinc-500 mt-0.5">
            {config.region && <span>{config.region}</span>}
            <span className="text-zinc-600">|</span>
            <span className="font-mono">{config.sport}</span>
            {config.created_at && (
              <>
                <span className="text-zinc-600">|</span>
                <span className="text-zinc-600">{new Date(config.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={handleRefreshFull}
            disabled={loadingPreview}
            className="p-1.5 text-zinc-500 hover:text-white transition-colors"
            title="Edit Games"
            data-testid="scoreboard-refresh-btn"
          >
            {loadingPreview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => onDelete(config.id)}
            className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
            title="Delete"
            data-testid="scoreboard-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* JSON URL */}
      <div className="flex items-center gap-2 bg-black/40 px-3 py-2">
        <Link2 className="w-3 h-3 text-zinc-600 shrink-0" />
        <span className="text-[11px] text-zinc-400 font-mono truncate flex-1">{jsonUrl}</span>
        <button
          onClick={() => copyToClipboard(jsonUrl)}
          className="p-1 text-zinc-500 hover:text-white transition-colors shrink-0"
          title="Copy JSON URL"
          data-testid="scoreboard-copy-url-btn"
        >
          <Copy className="w-3 h-3" />
        </button>
        <a
          href={jsonUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 text-zinc-500 hover:text-white transition-colors shrink-0"
          title="Open JSON"
        >
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Ticker URL */}
      <div className="flex items-center gap-2 bg-black/40 px-3 py-2">
        <Layers className="w-3 h-3 text-orange-500/60 shrink-0" />
        <span className="text-[11px] text-orange-400/80 font-mono truncate flex-1">{tickerUrl}</span>
        <button
          onClick={() => copyToClipboard(tickerUrl)}
          className="p-1 text-zinc-500 hover:text-orange-400 transition-colors shrink-0"
          title="Copy Ticker URL"
          data-testid="scoreboard-copy-ticker-btn"
        >
          <Copy className="w-3 h-3" />
        </button>
        <a
          href={tickerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 text-zinc-500 hover:text-orange-400 transition-colors shrink-0"
          title="Preview Ticker"
        >
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Inline Ticker Preview */}
      <div>
        <button
          onClick={() => { setShowTickerPreview(!showTickerPreview); setTickerKey(k => k + 1); }}
          className="flex items-center gap-2 w-full px-3 py-2 bg-black/30 hover:bg-black/50 transition-colors text-xs"
          data-testid="ticker-preview-toggle"
        >
          <Monitor className="w-3.5 h-3.5 text-orange-400/70" />
          <span className="text-zinc-400">{showTickerPreview ? "Hide" : "Show"} Live Ticker Preview</span>
          {showTickerPreview ? <ChevronUp className="w-3 h-3 text-zinc-600 ml-auto" /> : <ChevronDown className="w-3 h-3 text-zinc-600 ml-auto" />}
        </button>
        {showTickerPreview && (
          <div ref={tickerContainerRef} className="mt-1 bg-black border border-white/10 overflow-hidden" style={{ height: Math.ceil(67 * tickerScale) + 'px' }}>
            <div style={{ width: '1920px', height: '67px', transform: `scale(${tickerScale})`, transformOrigin: 'top left' }}>
              <iframe
                key={tickerKey}
                src={tickerUrl}
                title="Ticker Preview"
                width="1920"
                height="67"
                style={{ border: 'none', display: 'block' }}
                data-testid="ticker-preview-iframe"
              />
            </div>
          </div>
        )}
      </div>

      {/* Preview toggle */}
      {preview && (
        <div>
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-1 text-[10px] text-zinc-500 hover:text-white transition-colors"
            >
              {showPreview ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {rawGames.length} games {hiddenGames.length > 0 && `(${hiddenGames.length} hidden)`}
            </button>
            {hiddenGames.length > 0 && showPreview && (
              <button
                onClick={() => setShowHidden(!showHidden)}
                className="text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors"
              >
                {showHidden ? "Hide removed" : "Show removed"}
              </button>
            )}
          </div>
          {showPreview && (
            /* ── Full-screen Game Editor Modal ── */
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center" data-testid="game-editor-modal">
              <div className="bg-zinc-950 border border-white/10 w-[95vw] h-[90vh] flex flex-col overflow-hidden">
                {/* Modal header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 shrink-0">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {editingConfig ? (
                      <div className="flex flex-col gap-2 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            value={editingConfig.title}
                            onChange={(e) => setEditingConfig({ ...editingConfig, title: e.target.value })}
                            className="bg-zinc-900 border border-white/10 text-white px-2 py-1 text-sm font-bold w-64"
                            placeholder="Title — shown in ticker text transitions"
                            data-testid="edit-config-title"
                          />
                          <input
                            type="text"
                            value={editingConfig.region}
                            onChange={(e) => setEditingConfig({ ...editingConfig, region: e.target.value })}
                            className="bg-zinc-900 border border-white/10 text-white px-2 py-1 text-sm w-40"
                            placeholder="Region"
                            data-testid="edit-config-region"
                          />
                          <button onClick={saveConfigMeta} className="px-3 py-1 bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold transition-colors">Save</button>
                          <button onClick={() => setEditingConfig(null)} className="px-2 py-1 text-zinc-500 hover:text-white text-xs transition-colors">Cancel</button>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-zinc-500 uppercase tracking-wider whitespace-nowrap">Left Logo</span>
                            <input
                              type="text"
                              value={editingConfig.ticker_logo}
                              onChange={(e) => setEditingConfig({ ...editingConfig, ticker_logo: e.target.value })}
                              className="bg-zinc-900 border border-white/10 text-white px-2 py-1 text-xs font-mono w-56"
                              placeholder="https://example.com/logo.png"
                              data-testid="edit-config-ticker-logo"
                            />
                            {editingConfig.ticker_logo && (
                              <div className="w-7 h-7 bg-black/60 border border-white/10 flex items-center justify-center shrink-0">
                                <img src={editingConfig.ticker_logo} alt="" className="max-w-full max-h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-zinc-500 uppercase tracking-wider whitespace-nowrap">Sponsor</span>
                            <input
                              type="text"
                              value={editingConfig.ticker_sponsor}
                              onChange={(e) => setEditingConfig({ ...editingConfig, ticker_sponsor: e.target.value })}
                              className="bg-zinc-900 border border-white/10 text-white px-2 py-1 text-xs font-mono w-56"
                              placeholder="https://example.com/sponsor.png"
                              data-testid="edit-config-ticker-sponsor"
                            />
                            {editingConfig.ticker_sponsor && (
                              <div className="h-7 bg-black/60 border border-white/10 flex items-center justify-center shrink-0 px-1">
                                <img src={editingConfig.ticker_sponsor} alt="" className="max-h-full object-contain" style={{ maxWidth: 70 }} onError={(e) => { e.target.style.display = 'none'; }} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setEditingConfig({ title: config.title, region: config.region || '', ticker_logo: config.ticker_logo || '', ticker_sponsor: config.ticker_sponsor || '' })} title="Click to edit title, region, logos & groups">
                        {config.ticker_logo && (
                          <div className="w-6 h-6 bg-black/40 border border-white/10 flex items-center justify-center shrink-0">
                            <img src={config.ticker_logo} alt="" className="max-w-full max-h-full object-contain" />
                          </div>
                        )}
                        <h3 className="text-white text-sm font-bold group-hover:text-orange-400 transition-colors">{config.title}</h3>
                        {config.region && <span className="text-zinc-500 text-xs">{config.region}</span>}
                        {config.ticker_sponsor && (
                          <div className="h-5 bg-black/40 border border-white/10 flex items-center justify-center shrink-0 px-1">
                            <img src={config.ticker_sponsor} alt="" className="max-h-full object-contain" style={{ maxWidth: 50 }} />
                          </div>
                        )}
                        <Edit3 className="w-3 h-3 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    )}
                    {/* Group membership badges */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      {memberGroups.map(g => (
                        <span key={g.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 border border-orange-500/30 text-orange-400 text-[10px] font-bold uppercase tracking-wider">
                          {g.title}
                          <button onClick={() => toggleGroupMembership(g, false)} className="ml-0.5 hover:text-red-400 transition-colors" title={`Remove from "${g.title}"`}>&times;</button>
                        </span>
                      ))}
                      {nonMemberGroups.length > 0 && (
                        <div className="relative">
                          <select
                            className="bg-zinc-900 border border-white/10 text-zinc-400 text-[10px] px-1.5 py-0.5 pr-5 appearance-none cursor-pointer hover:border-orange-500/50 transition-colors"
                            value=""
                            onChange={(e) => {
                              const grp = nonMemberGroups.find(g => g.id === e.target.value);
                              if (grp) toggleGroupMembership(grp, true);
                            }}
                            data-testid="add-to-group-select"
                          >
                            <option value="">+ Group</option>
                            {nonMemberGroups.map(g => <option key={g.id} value={g.id}>{g.title}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                    <span className="text-zinc-600 text-xs shrink-0">{rawGames.length} games {hiddenGames.length > 0 && `(${hiddenGames.length} hidden)`}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {hiddenGames.length > 0 && (
                      <button
                        onClick={() => setShowHidden(!showHidden)}
                        className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors"
                      >
                        {showHidden ? "Hide removed" : "Show removed"}
                      </button>
                    )}
                    <button
                      onClick={() => { setShowPreview(false); setEditingGame(null); setSavedGame(null); }}
                      className="p-1.5 text-zinc-500 hover:text-white transition-colors"
                      title="Close"
                      data-testid="close-game-editor"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Modal body — game list on left, edit panel on right */}
                <div className="flex flex-1 overflow-hidden">
                  {/* Game List */}
                  <div className={`${editingGame ? 'w-[340px] border-r border-white/10' : 'flex-1'} overflow-y-auto`}>
                    {rawGames.map((g, i) => {
                      const hidden = isHidden(g);
                      const hasOv = hasOverride(g);
                      const isActive = editingGame === g.game_url;

                      if (hidden && !showHidden) return null;

                      return (
                        <div
                          key={g.game_url || i}
                          className={`text-xs px-4 py-3 border-b border-white/5 cursor-pointer transition-colors ${
                            isActive ? "bg-orange-500/10 border-l-2 border-l-orange-500" : "hover:bg-white/5"
                          } ${hidden ? "opacity-40 line-through" : ""} ${hasOv && !isActive ? "border-l-2 border-l-orange-500/50" : ""}`}
                          onClick={() => !hidden && startEditing(g)}
                          data-testid={`game-row-${i}`}
                        >
                          <div className="flex items-center gap-2">
                            {(overrides[g.game_url]?.featured) && (
                              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400 shrink-0" title="Featured Game" />
                            )}
                            <div className="flex items-center gap-1 shrink-0">
                              <div className="w-3.5 h-3.5 border border-white/10" style={{ background: (overrides[g.game_url]?.away_color || g.away_color) || '#333' }} />
                              {g.away_logo && <img src={resolveLogoSrc(overrides[g.game_url]?.away_logo || g.away_logo)} alt="" className="w-4 h-4 object-contain" />}
                            </div>
                            <span className="text-zinc-400 flex-1 truncate">
                              {getDisplayName(g, "away_team")} {g.away_score != null ? g.away_score : ""} @ {getDisplayName(g, "home_team")} {g.home_score != null ? g.home_score : ""}
                            </span>
                            <div className="flex items-center gap-1 shrink-0">
                              {g.home_logo && <img src={resolveLogoSrc(overrides[g.game_url]?.home_logo || g.home_logo)} alt="" className="w-4 h-4 object-contain" />}
                              <div className="w-3.5 h-3.5 border border-white/10" style={{ background: (overrides[g.game_url]?.home_color || g.home_color) || '#333' }} />
                            </div>
                            <span className={`text-[10px] font-bold shrink-0 ${
                              g.state === "final" ? "text-zinc-500" : g.state === "live" ? "text-green-400" : "text-zinc-600"
                            }`}>{g.status}</span>
                            <div className="flex gap-0.5 shrink-0 ml-1" onClick={(e) => e.stopPropagation()}>
                              {hidden ? (
                                <button onClick={() => restoreGame(g.game_url)} className="p-1 text-zinc-600 hover:text-green-400 transition-colors" title="Restore">
                                  <RotateCcw className="w-3 h-3" />
                                </button>
                              ) : (
                                <>
                                  <button onClick={() => moveGame(i, -1)} disabled={i === 0} className="p-0.5 text-zinc-600 hover:text-blue-400 disabled:opacity-20" title="Move up" data-testid={`move-up-${i}`}>
                                    <ArrowUp className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => moveGame(i, 1)} disabled={i === rawGames.length - 1} className="p-0.5 text-zinc-600 hover:text-blue-400 disabled:opacity-20" title="Move down" data-testid={`move-down-${i}`}>
                                    <ArrowDown className="w-3 h-3" />
                                  </button>
                                  <button onClick={() => hideGame(g.game_url)} className="p-0.5 text-zinc-600 hover:text-red-400" title="Hide" data-testid={`hide-game-${i}`}>
                                    <EyeOff className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {rawGames.length === 0 && (
                      <p className="text-zinc-600 text-xs py-8 text-center">No games found for this date.</p>
                    )}
                  </div>

                  {/* Edit Panel — right side */}
                  {editingGame && (
                    <div className="flex-1 overflow-y-auto p-6">
                      {(() => {
                        const g = rawGames.find(game => game.game_url === editingGame);
                        if (!g) return <p className="text-zinc-600 text-sm">Game not found</p>;
                        return (
                          <div className="max-w-2xl space-y-5">
                            <h4 className="text-white text-sm font-bold">
                              {getDisplayName(g, "away_team")} @ {getDisplayName(g, "home_team")}
                            </h4>

                            {/* Team Names */}
                            <div>
                              <h5 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Team Names</h5>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs text-zinc-500 mb-1 block">Away Team</label>
                                  <input
                                    type="text"
                                    value={editForm.away_team}
                                    onChange={(e) => { setEditForm({ ...editForm, away_team: e.target.value }); setSavedGame(null); }}
                                    className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                                    data-testid="edit-away-name"
                                  />
                                </div>
                                <div>
                                  <label className="text-xs text-zinc-500 mb-1 block">Home Team</label>
                                  <input
                                    type="text"
                                    value={editForm.home_team}
                                    onChange={(e) => { setEditForm({ ...editForm, home_team: e.target.value }); setSavedGame(null); }}
                                    className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
                                    data-testid="edit-home-name"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Colors */}
                            <div>
                              <h5 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1"><Palette className="w-3 h-3" /> Team Colors</h5>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs text-zinc-500 mb-1 block">Away Color</label>
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 border border-white/10 shrink-0" style={{ background: editForm._orig_away_color || '#333' }} title="Original" />
                                    <span className="text-zinc-600">→</span>
                                    <input
                                      type="color"
                                      value={editForm.away_color || '#333333'}
                                      onChange={(e) => { setEditForm({ ...editForm, away_color: e.target.value }); setSavedGame(null); }}
                                      className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer shrink-0"
                                      data-testid="edit-away-color"
                                    />
                                    <input
                                      type="text"
                                      value={editForm.away_color || ''}
                                      onChange={(e) => { setEditForm({ ...editForm, away_color: e.target.value }); setSavedGame(null); }}
                                      placeholder="#hex"
                                      className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm font-mono"
                                    />
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs text-zinc-500 mb-1 block">Home Color</label>
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 border border-white/10 shrink-0" style={{ background: editForm._orig_home_color || '#333' }} title="Original" />
                                    <span className="text-zinc-600">→</span>
                                    <input
                                      type="color"
                                      value={editForm.home_color || '#333333'}
                                      onChange={(e) => { setEditForm({ ...editForm, home_color: e.target.value }); setSavedGame(null); }}
                                      className="w-10 h-10 bg-transparent border border-white/10 cursor-pointer shrink-0"
                                      data-testid="edit-home-color"
                                    />
                                    <input
                                      type="text"
                                      value={editForm.home_color || ''}
                                      onChange={(e) => { setEditForm({ ...editForm, home_color: e.target.value }); setSavedGame(null); }}
                                      placeholder="#hex"
                                      className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm font-mono"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Logos */}
                            <div>
                              <h5 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Team Logos</h5>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="text-xs text-zinc-500 mb-1 block">Away Logo</label>
                                  <div className="flex items-center gap-3 mb-2">
                                    {editForm._orig_away_logo && (
                                      <div className="w-14 h-14 bg-white/90 border border-white/10 flex items-center justify-center shrink-0" title="Current">
                                        <img src={resolveLogoSrc(editForm._orig_away_logo)} alt="" className="max-w-[48px] max-h-[48px] object-contain" />
                                      </div>
                                    )}
                                    {editForm.away_logo && editForm.away_logo !== editForm._orig_away_logo && (
                                      <>
                                        <span className="text-zinc-600">→</span>
                                        <div className="w-14 h-14 bg-white/90 border border-orange-500/50 flex items-center justify-center shrink-0" title="New">
                                          <img src={resolveLogoSrc(editForm.away_logo)} alt="" className="max-w-[48px] max-h-[48px] object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={editForm.away_logo || ''}
                                      onChange={(e) => { setEditForm({ ...editForm, away_logo: e.target.value }); setSavedGame(null); }}
                                      placeholder="Paste logo URL..."
                                      className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm font-mono min-w-0"
                                      data-testid="edit-away-logo"
                                    />
                                    <label className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs cursor-pointer transition-colors border border-white/10 shrink-0 flex items-center gap-1.5" data-testid="upload-away-logo">
                                      <ImageIcon className="w-3.5 h-3.5" /> Upload
                                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { handleLogoUpload(e.target.files[0], 'away'); setSavedGame(null); }} />
                                    </label>
                                  </div>
                                </div>
                                <div>
                                  <label className="text-xs text-zinc-500 mb-1 block">Home Logo</label>
                                  <div className="flex items-center gap-3 mb-2">
                                    {editForm._orig_home_logo && (
                                      <div className="w-14 h-14 bg-white/90 border border-white/10 flex items-center justify-center shrink-0" title="Current">
                                        <img src={resolveLogoSrc(editForm._orig_home_logo)} alt="" className="max-w-[48px] max-h-[48px] object-contain" />
                                      </div>
                                    )}
                                    {editForm.home_logo && editForm.home_logo !== editForm._orig_home_logo && (
                                      <>
                                        <span className="text-zinc-600">→</span>
                                        <div className="w-14 h-14 bg-white/90 border border-orange-500/50 flex items-center justify-center shrink-0" title="New">
                                          <img src={resolveLogoSrc(editForm.home_logo)} alt="" className="max-w-[48px] max-h-[48px] object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={editForm.home_logo || ''}
                                      onChange={(e) => { setEditForm({ ...editForm, home_logo: e.target.value }); setSavedGame(null); }}
                                      placeholder="Paste logo URL..."
                                      className="flex-1 bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm font-mono min-w-0"
                                      data-testid="edit-home-logo"
                                    />
                                    <label className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs cursor-pointer transition-colors border border-white/10 shrink-0 flex items-center gap-1.5" data-testid="upload-home-logo">
                                      <ImageIcon className="w-3.5 h-3.5" /> Upload
                                      <input type="file" accept="image/*" className="hidden" onChange={(e) => { handleLogoUpload(e.target.files[0], 'home'); setSavedGame(null); }} />
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Featured Game Toggle */}
                            <div className="pt-3 border-t border-white/5">
                              <button
                                type="button"
                                onClick={() => { setEditForm({ ...editForm, featured: !editForm.featured }); setSavedGame(null); }}
                                className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase transition-colors border ${
                                  editForm.featured
                                    ? "bg-yellow-500/20 border-yellow-500/50 text-yellow-400"
                                    : "bg-zinc-900 border-white/10 text-zinc-500 hover:text-zinc-300"
                                }`}
                                data-testid="toggle-featured"
                              >
                                <Star className={`w-4 h-4 ${editForm.featured ? "fill-yellow-400" : ""}`} />
                                {editForm.featured ? "Featured Game" : "Set as Featured"}
                              </button>
                              {editForm.featured && (
                                <p className="text-xs text-yellow-500/60 mt-1.5">Score changes trigger a flash alert on the ticker</p>
                              )}
                            </div>

                            {/* Save / Done / Cancel */}
                            <div className="flex gap-3 pt-3 border-t border-white/5">
                              {savedGame === editingGame ? (
                                <button
                                  onClick={() => { setEditingGame(null); setSavedGame(null); }}
                                  className="px-5 py-2 bg-green-600 hover:bg-green-500 text-white text-xs font-bold transition-colors flex items-center gap-2"
                                  data-testid="done-edit"
                                >
                                  <Check className="w-4 h-4" /> Done
                                </button>
                              ) : (
                                <button
                                  onClick={saveGameEdit}
                                  disabled={savingOverrides}
                                  className="px-5 py-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold transition-colors"
                                  data-testid="save-edit"
                                >
                                  {savingOverrides ? "Saving..." : "Save"}
                                </button>
                              )}
                              <button
                                onClick={() => { setEditingGame(null); setSavedGame(null); }}
                                className="px-4 py-2 text-zinc-500 hover:text-white text-xs transition-colors"
                              >Cancel</button>
                              {hasOverride(g) && (
                                <button
                                  onClick={() => { clearGameOverride(g.game_url); setEditingGame(null); setSavedGame(null); }}
                                  className="px-4 py-2 text-red-500 hover:text-red-400 text-xs transition-colors ml-auto"
                                >Reset to Original</button>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* No game selected prompt */}
                  {!editingGame && (
                    <div className="hidden" />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {/* Custom Ticker Text Lines */}
      <div className="border-t border-white/5 pt-3 mt-2">
        <h5 className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <Type className="w-3 h-3" /> Custom Ticker Text
        </h5>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCustomText()}
            placeholder="Enter scrolling text line..."
            className="flex-1 bg-black/40 border border-white/10 text-white px-3 py-1.5 text-xs"
            data-testid="custom-text-input"
          />
          <button
            onClick={addCustomText}
            className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs transition-colors"
            data-testid="custom-text-add-btn"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
        {customTexts.length > 0 && (
          <div className="space-y-1">
            {customTexts.map((text, i) => (
              <div key={i} className="flex items-center gap-2 bg-black/30 px-3 py-1.5 text-xs">
                <span className="text-cyan-400/80 flex-1 truncate">{text}</span>
                <button
                  onClick={() => removeCustomText(i)}
                  className="p-1 text-zinc-600 hover:text-red-400 transition-colors shrink-0"
                  data-testid={`custom-text-del-${i}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Group Form ── */
const GroupForm = ({ scoreboards, onSave, initial = null, onCancel }) => {
  const [title, setTitle] = useState(initial?.title || "");
  const [selectedIds, setSelectedIds] = useState(initial?.scoreboard_ids || []);
  const [rssFeeds, setRssFeeds] = useState(initial?.rss_feeds || []);
  const [newRssTitle, setNewRssTitle] = useState("");
  const [newRssUrl, setNewRssUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleId = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addRssFeed = () => {
    if (!newRssTitle.trim() || !newRssUrl.trim()) { toast.error("RSS feed needs a title and URL"); return; }
    setRssFeeds([...rssFeeds, { title: newRssTitle.trim(), url: newRssUrl.trim() }]);
    setNewRssTitle("");
    setNewRssUrl("");
  };

  const removeRssFeed = (idx) => {
    setRssFeeds(rssFeeds.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title) { toast.error("Group title is required"); return; }
    if (selectedIds.length === 0 && rssFeeds.length === 0) { toast.error("Add at least one ticker or RSS feed"); return; }
    setSaving(true);
    try {
      await onSave({ title, scoreboard_ids: selectedIds, rss_feeds: rssFeeds });
      if (!initial) { setTitle(""); setSelectedIds([]); }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 bg-zinc-900/60 border border-white/10 p-4" data-testid="scoreboard-group-form">
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Group Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Friday Night Scoreboard"
          className="w-full bg-black/40 border border-white/10 text-white px-3 py-2 text-sm"
          data-testid="group-title-input"
        />
      </div>
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Select Tickers to Merge</label>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {scoreboards.map((sb) => (
            <label key={sb.id} className="flex items-center gap-2 px-3 py-2 bg-black/30 hover:bg-white/5 cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={selectedIds.includes(sb.id)}
                onChange={() => toggleId(sb.id)}
                className="accent-white"
              />
              <span className="text-white text-xs">{sb.title}</span>
              <span className="text-zinc-600 text-[10px] ml-auto font-mono">{sb.sport}</span>
            </label>
          ))}
          {scoreboards.length === 0 && (
            <p className="text-zinc-600 text-xs py-2">Create tickers first to group them.</p>
          )}
        </div>
      </div>

      {/* RSS Feeds */}
      <div>
        <label className="block text-[10px] text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
          <Rss className="w-3 h-3" /> RSS Headline Feeds
        </label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newRssTitle}
            onChange={(e) => setNewRssTitle(e.target.value)}
            placeholder="Feed title (e.g., Headlines)"
            className="w-1/3 bg-black/40 border border-white/10 text-white px-3 py-1.5 text-xs"
            data-testid="rss-title-input"
          />
          <input
            type="text"
            value={newRssUrl}
            onChange={(e) => setNewRssUrl(e.target.value)}
            placeholder="RSS feed URL"
            className="flex-1 bg-black/40 border border-white/10 text-white px-3 py-1.5 text-xs"
            data-testid="rss-url-input"
          />
          <button
            type="button"
            onClick={addRssFeed}
            className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs transition-colors"
            data-testid="rss-add-btn"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
        {rssFeeds.length > 0 && (
          <div className="space-y-1">
            {rssFeeds.map((feed, i) => (
              <div key={i} className="flex items-center gap-2 bg-black/30 px-3 py-1.5 text-xs">
                <Rss className="w-3 h-3 text-orange-500/60 shrink-0" />
                <span className="text-white font-bold shrink-0">{feed.title}</span>
                <span className="text-zinc-500 font-mono truncate flex-1">{feed.url}</span>
                <button
                  type="button"
                  onClick={() => removeRssFeed(i)}
                  className="p-1 text-zinc-600 hover:text-red-400 transition-colors shrink-0"
                  data-testid={`rss-del-${i}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm transition-colors"
          data-testid="group-save-btn"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers className="w-3 h-3" />}
          {initial ? "Update Group" : "Create Group"}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-zinc-500 hover:text-white px-4 py-2 text-sm transition-colors">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

/* ── Group Card ── */
const GroupCard = ({ group, scoreboards, onDelete }) => {
  const jsonUrl = `${process.env.REACT_APP_BACKEND_URL}/api/scoreboard/group/${group.id}/scores.json`;
  const tickerUrl = `${process.env.REACT_APP_BACKEND_URL}/api/scoreboard/group/${group.id}/ticker.html`;
  const [showTickerPreview, setShowTickerPreview] = useState(false);
  const [tickerKey, setTickerKey] = useState(0);
  const [tickerScale, setTickerScale] = useState(0.5);
  const tickerContainerRef = useRef(null);
  const memberNames = group.scoreboard_ids
    ?.map((id) => scoreboards.find((s) => s.id === id)?.title)
    .filter(Boolean) || [];

  useEffect(() => {
    if (!showTickerPreview || !tickerContainerRef.current) return;
    const measure = () => {
      const w = tickerContainerRef.current?.clientWidth;
      if (w) setTickerScale(w / 1920);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(tickerContainerRef.current);
    return () => ro.disconnect();
  }, [showTickerPreview]);

  return (
    <div className="bg-zinc-900/60 border border-white/10 p-4 space-y-3" data-testid={`scoreboard-group-${group.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="text-white text-sm font-bold flex items-center gap-2">
            <Layers className="w-3.5 h-3.5 text-zinc-400" />
            {group.title}
          </h4>
          <p className="text-[10px] text-zinc-500 mt-0.5">
            Merges: {memberNames.join(", ") || "No scoreboards selected"}
            {(group.rss_feeds?.length > 0) && (
              <span className="text-orange-500/60 ml-1">+ {group.rss_feeds.length} RSS feed{group.rss_feeds.length > 1 ? 's' : ''}</span>
            )}
            {group.created_at && (
              <span className="text-zinc-600 ml-2">| {new Date(group.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
            )}
          </p>
        </div>
        <button
          onClick={() => onDelete(group.id)}
          className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors shrink-0"
          title="Delete Group"
          data-testid="group-delete-btn"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex items-center gap-2 bg-black/40 px-3 py-2">
        <Link2 className="w-3 h-3 text-zinc-600 shrink-0" />
        <span className="text-[11px] text-zinc-400 font-mono truncate flex-1">{jsonUrl}</span>
        <button
          onClick={() => copyToClipboard(jsonUrl)}
          className="p-1 text-zinc-500 hover:text-white transition-colors shrink-0"
          title="Copy JSON URL"
        >
          <Copy className="w-3 h-3" />
        </button>
        <a href={jsonUrl} target="_blank" rel="noopener noreferrer" className="p-1 text-zinc-500 hover:text-white transition-colors shrink-0">
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Ticker URL */}
      <div className="flex items-center gap-2 bg-black/40 px-3 py-2">
        <Layers className="w-3 h-3 text-orange-500/60 shrink-0" />
        <span className="text-[11px] text-orange-400/80 font-mono truncate flex-1">{tickerUrl}</span>
        <button
          onClick={() => copyToClipboard(tickerUrl)}
          className="p-1 text-zinc-500 hover:text-orange-400 transition-colors shrink-0"
          title="Copy Ticker URL"
          data-testid="group-copy-ticker-btn"
        >
          <Copy className="w-3 h-3" />
        </button>
        <a
          href={tickerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1 text-zinc-500 hover:text-orange-400 transition-colors shrink-0"
          title="Preview Ticker"
        >
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Inline Ticker Preview */}
      <div>
        <button
          onClick={() => { setShowTickerPreview(!showTickerPreview); setTickerKey(k => k + 1); }}
          className="flex items-center gap-2 w-full px-3 py-2 bg-black/30 hover:bg-black/50 transition-colors text-xs"
          data-testid="group-ticker-preview-toggle"
        >
          <Monitor className="w-3.5 h-3.5 text-orange-400/70" />
          <span className="text-zinc-400">{showTickerPreview ? "Hide" : "Show"} Live Ticker Preview</span>
          {showTickerPreview ? <ChevronUp className="w-3 h-3 text-zinc-600 ml-auto" /> : <ChevronDown className="w-3 h-3 text-zinc-600 ml-auto" />}
        </button>
        {showTickerPreview && (
          <div ref={tickerContainerRef} className="mt-1 bg-black border border-white/10 overflow-hidden" style={{ height: Math.ceil(67 * tickerScale) + 'px' }}>
            <div style={{ width: '1920px', height: '67px', transform: `scale(${tickerScale})`, transformOrigin: 'top left' }}>
              <iframe
                key={tickerKey}
                src={tickerUrl}
                title="Group Ticker Preview"
                width="1920"
                height="67"
                style={{ border: 'none', display: 'block' }}
                data-testid="group-ticker-preview-iframe"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Main Tab ── */
export const ScoreboardTab = () => {
  const [configs, setConfigs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [cfgRes, grpRes] = await Promise.all([
        axios.get(`${API}/scoreboard/configs`, { headers: getAuthHeader() }),
        axios.get(`${API}/scoreboard/groups`, { headers: getAuthHeader() }),
      ]);
      setConfigs(cfgRes.data);
      setGroups(grpRes.data);
    } catch (err) {
      toast.error("Failed to load tickers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateConfig = async (form) => {
    try {
      await axios.post(`${API}/scoreboard/configs`, form, { headers: getAuthHeader() });
      toast.success("Ticker created");
      setShowAddForm(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to create ticker");
    }
  };

  const handleDeleteConfig = async (id) => {
    if (!window.confirm("Delete this ticker?")) return;
    try {
      await axios.delete(`${API}/scoreboard/configs/${id}`, { headers: getAuthHeader() });
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleCreateGroup = async (form) => {
    try {
      await axios.post(`${API}/scoreboard/groups`, form, { headers: getAuthHeader() });
      toast.success("Group created");
      setShowGroupForm(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to create group");
    }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm("Delete this group?")) return;
    try {
      await axios.delete(`${API}/scoreboard/groups/${id}`, { headers: getAuthHeader() });
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="scoreboard-tab">
      {/* Header */}
      <div>
        <h2 className="text-white text-lg font-bold">Ticker</h2>
        <p className="text-zinc-500 text-xs mt-1">
          Add MaxPreps score page links to generate JSON feeds. Group multiple to merge them into one feed.
        </p>
      </div>

      {/* Tickers Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Tickers</h3>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"
            data-testid="add-scoreboard-btn"
          >
            <Plus className="w-3 h-3" /> Add
          </button>
        </div>

        {showAddForm && (
          <ConfigForm onSave={handleCreateConfig} onCancel={() => setShowAddForm(false)} />
        )}

        {configs.length === 0 && !showAddForm && (
          <p className="text-zinc-600 text-xs py-4 text-center">No tickers yet. Click "Add" to create one.</p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {configs.map((cfg) => (
            <ConfigCard
              key={cfg.id}
              config={cfg}
              groups={groups}
              onDelete={handleDeleteConfig}
              onRefresh={fetchData}
              onUpdate={fetchData}
            />
          ))}
        </div>
      </div>

      {/* Groups Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-zinc-400 text-xs font-bold uppercase tracking-widest">Merged Groups</h3>
          <button
            onClick={() => setShowGroupForm(!showGroupForm)}
            className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white transition-colors"
            data-testid="add-group-btn"
          >
            <Plus className="w-3 h-3" /> Create Group
          </button>
        </div>

        {showGroupForm && (
          <GroupForm scoreboards={configs} onSave={handleCreateGroup} onCancel={() => setShowGroupForm(false)} />
        )}

        {groups.length === 0 && !showGroupForm && (
          <p className="text-zinc-600 text-xs py-4 text-center">No groups yet. Create a group to merge multiple tickers into one JSON feed.</p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {groups.map((grp) => (
            <GroupCard
              key={grp.id}
              group={grp}
              scoreboards={configs}
              onDelete={handleDeleteGroup}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScoreboardTab;
